import type { ApprovalActionContext } from "@carbon-operations/contracts";

import { AgentActionFacts } from "./agent-action-facts.js";

function Definition({ term, value }: { term: string; value: string }) {
  return (
    <div className="definition">
      <dt>{term}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/** Human-readable rendering of the exact server-derived object hashed for approval. */
export function ApprovalActionContextFacts({ context }: { context: ApprovalActionContext }) {
  return (
    <section className="approval-action-context" aria-label="Exact action context">
      <p>
        These server-derived facts are the complete action context bound to the
        expected action SHA-256.
      </p>
      {context.execution.kind === "authorization_receipt" && (
        <p className="read-only-note">
          Authorization receipt only. OperationSV does not execute this action.
        </p>
      )}
      <dl className="agent-action-facts">
        <Definition term="Context contract" value={`${context.schema} · API v${context.contract_version}`} />
        <Definition term="Tenant" value={context.tenant_id} />
        <Definition term="Workspace" value={context.workspace_id} />
        <Definition term="Capability" value={`${context.capability_id} v${context.capability_version}`} />
        <Definition term="Requested by" value={context.requester.actor_id} />
        <Definition
          term="On behalf of"
          value={`${context.requester.on_behalf_of.subject_type} · ${context.requester.on_behalf_of.subject_id}`}
        />
        <Definition term="Goal" value={context.goal} />
        <Definition term="Autonomy" value={context.requested_autonomy} />
        <Definition term="Event sink" value={context.event_sink_id} />
        <Definition term="Run expires" value={new Date(context.run_expires_at).toLocaleString()} />
        <Definition term="Execution" value={context.execution.kind} />
        {context.execution.kind === "document_draft" && (
          <>
            <Definition term="Template" value={context.execution.template_version} />
            <Definition term="Output media" value={context.execution.output_media_type} />
          </>
        )}
      </dl>
      {context.inputs.length === 0
        ? <p>No input artifacts are part of this action.</p>
        : (
          <div className="evidence-ledger">
            <h3>Bound inputs</h3>
            {context.inputs.map((input) => (
              <div key={`${input.name}:${input.ref}`}>
                <strong>{input.name}</strong>
                <p>{input.ref} · {input.media_type}</p>
                <code>{input.sha256}</code>
              </div>
            ))}
          </div>
        )}
      {context.execution.kind === "authorization_receipt" && (
        <AgentActionFacts action={context.execution.agent_action} />
      )}
    </section>
  );
}
